'use client';

import type { SubmitHandler } from 'react-hook-form';

import React, { useCallback, useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as z from 'zod';

import {
  Button,
  Form,
  AuthContainer,
  Typography
} from '@goodneighbor/admin-ui/server';

import { useAdminLoginWithPersistence } from './useAdminLoginWithPersistence';

const ON_CHANGE_TEXT_ERROR_DELAY = 2000;

const schema = z.object({
  phone: z.string()
});

type LoginWithPhoneFormSchema = z.infer<typeof schema>;

const Signin = () => {
  const router = useRouter();

  useEffect(() => {
    router.prefetch('/auth/send-otp');
  }, [router]);

  const { handleLogInOtpWithPersistance, status } =
    useAdminLoginWithPersistence();
  const methods = useForm<LoginWithPhoneFormSchema>({
    mode: 'onChange',
    delayError: ON_CHANGE_TEXT_ERROR_DELAY,
    resolver: zodResolver(schema),
    defaultValues: {
      phone: undefined
    }
  });

  const handleSubmit: SubmitHandler<LoginWithPhoneFormSchema> = useCallback(
    async (data) => {
      await handleLogInOtpWithPersistance({
        countryCode: 'US',
        phone: data.phone
      })
        .then(() => {
          router.push(
            `/auth/send-otp?phoneNumber=${encodeURIComponent(data.phone)}`
          );
        })
        .catch((error) => {
          toast.error('Something went wrong', {
            description: error.message || error.response.data
          });
        });
    },
    [handleLogInOtpWithPersistance, router]
  );

  return (
    <AuthContainer>
      <div className={'flex items-center justify-center flex-col gap-2 pb-8'}>
        <Typography
          className={'text-gray-500 text-center'}
          fontFace={'BodyRegularMd'}
        >
          {
            "Log in by entering your phone number, we'll send you an SMS with a verification code."
          }
        </Typography>
      </div>
      <Form
        methods={methods}
        onSubmit={methods.handleSubmit(handleSubmit)}
        className={'flex-col flex gap-6'}
      >
        <Form.Item name={'phone'}>
          <Form.Input placeholder={'Phone Number'} />
          <Form.Error />
        </Form.Item>
        <Button
          className={'w-full'}
          buttonType={'brand'}
          type={'submit'}
          loading={status === 'loading'}
        >
          {'Send Code'}
        </Button>
      </Form>
    </AuthContainer>
  );
};

export default Signin;
